import { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

const API_URL = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';

/**
 * Hook to track real-time online visitors for a specific vendor
 * @param vendorAlias - The vendor alias to track visitors for
 * @returns The current number of online visitors
 */
export const useVisitorTracking = (vendorAlias: string | undefined): number => {
  const [visitorCount, setVisitorCount] = useState<number>(0);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Don't connect if no vendor alias
    if (!vendorAlias) {
      return;
    }

    console.log('[Backoffice VisitorTracking] Connecting for vendor:', vendorAlias);

    // Initialize socket connection
    const socket = io(`${API_URL}/visitors`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Backoffice VisitorTracking] Connected to server');

      // Subscribe to this vendor's visitor updates
      socket.emit('backoffice:subscribe', { vendorAlias }, (response: any) => {
        console.log('[Backoffice VisitorTracking] Subscribe response:', response);
        if (response?.count !== undefined) {
          setVisitorCount(response.count);
        }
      });
    });

    socket.on('disconnect', () => {
      console.log('[Backoffice VisitorTracking] Disconnected from server');
    });

    socket.on('connect_error', (error) => {
      console.error('[Backoffice VisitorTracking] Connection error:', error);
    });

    // Listen for visitor count updates
    socket.on('visitor:count', (data: { vendorAlias: string; count: number }) => {
      console.log('[Backoffice VisitorTracking] Received count update:', data);
      if (data.vendorAlias === vendorAlias) {
        setVisitorCount(data.count);
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('[Backoffice VisitorTracking] Cleaning up for vendor:', vendorAlias);

      if (socket.connected) {
        socket.emit('backoffice:unsubscribe', { vendorAlias });
      }

      socket.disconnect();
      socketRef.current = null;
    };
  }, [vendorAlias]);

  return visitorCount;
};
